import Store, { SessionStore } from "../../shared/functions/store";
import { callService, requestAPI } from "@phoenixreisen/gateway";
import { jwtDecode } from "jwt-decode";
import isInt from "validator/lib/isInt";
import User from "@phoenixreisen/user";
import Config from "../../config";
import DT from "date-and-time";
let bookingRequest = null;
export const Booking = {
    data: null,
    size: null,
    changed: null,
    getBookingNr,
    getBookingByNr,
};
export function loadBookingFromCache() {
    return Store.get(Config.storageKeyBooking);
}
export function persist(booking) {
    Store.set(Config.storageKeyBooking, {
        data: (booking && booking.data) || null,
        size: (booking && booking.size) || undefined,
        changed: (booking && booking.changed) || undefined,
    });
}
export function hasCacheExpired(changed) {
    const today = new Date();
    const expire = new Date(today.setMinutes(today.getMinutes() - 5));
    return !changed || new Date(changed) < expire;
}
export function isPhxAbbr(rechnr) {
    return !isInt(rechnr);
}
export function isGuestUser(rechnr) {
    return ((rechnr >= 900000 && rechnr <= 999999) ||
        location.href.includes("login/#!/kennenlernen"));
}
export function getStartInDays(booking) {
    const today = booking?.["buchung/buchungsstand"]
        ? DT.parse(booking["buchung/buchungsstand"], "DD.MM.YYYY HH:mm")
        : new Date();
    const cruisetime = booking["buchung/reisezeitraum"] ?? null;
    const startDateAsString = cruisetime?.[0] ?? null;
    if (startDateAsString) {
        const startDate = DT.parse(startDateAsString, "DD.MM.YYYY");
        const diff = startDate.getTime() - today.getTime();
        return Math.ceil(diff / (1000 * 3600 * 24));
    }
    return 0;
}
export const getRouteNames = (booking) => {
    const names = [];
    const routes = booking["buchung/routen"] ?? [];
    for (const route of routes) {
        const ship = route["route/schiff"];
        const code = route["route/route"];
        if (ship && code) {
            const name = `${ship}${code}`;
            names.push(name);
        }
    }
    return names;
};
export function startsInLessThanDays(booking, days) {
    const now = new Date();
    const dateStrings = booking["buchung/reisezeitraum"];
    const startDateString = dateStrings?.[0] ?? null;
    const startDate = (startDateString && DT.parse(startDateString, "DD.MM.YYYY")) || null;
    if (startDate) {
        const diff = Math.round(DT.subtract(startDate, now).toDays());
        return diff <= days;
    }
    return false;
}
export function isBookingStatusOk(booking) {
    return booking?.["buchung/status"] === "ok";
}
export function isBookingStatusBeendet(booking) {
    return booking?.["buchung/status"] === "reise-beendet";
}
export function isBookingStatusUnvollstaendig(booking) {
    return booking?.["buchung/status"] === "unvollstaendig";
}
export function alreadyCruised(booking) {
    const range = booking["buchung/reisezeitraum"] ?? null;
    if (range) {
        const till = DT.addDays(DT.parse(range[1], "DD.MM.YYYY"), 1);
        const now = DT.parse(booking["buchung/buchungsstand"], "DD.MM.YYYY HH:mm");
        return till <= now;
    }
    return false;
}
export function isPastAusschiffung(booking) {
    const range = booking["buchung/reisezeitraum"] ?? null;
    if (range) {
        const till = DT.addDays(DT.parse(range[1], "DD.MM.YYYY"), 0);
        const now = DT.parse(booking["buchung/buchungsstand"], "DD.MM.YYYY HH:mm");
        return till <= now;
    }
    return false;
}
export function hasBeenReversed(booking) {
    const storno = booking["buchung/storno-datum"] ?? null;
    return (storno && DT.parse(storno, "DD.MM.YYYY HH:mm")) || null;
}
export function hasCanceledFlag(booking) {
    return !!booking["buchung/reise-abgesagt?"];
}
export function getType(User) {
    if (User.jwt) {
        const data = jwtDecode(User.jwt);
        return data.kind;
    }
    return null;
}
export function getPhxId(User) {
    if (!User.isPhx()) {
        throw "Not a Phx User. You can check that with User.isPhx()";
    }
    const data = jwtDecode(User.jwt);
    return data.sub || undefined;
}
export function getServiceProviderId(User) {
    if (!User.isServiceProvider()) {
        throw "Not a Service Provider. You can check that with User.isServiceProvider()";
    }
    const data = jwtDecode(User.jwt);
    return data.anbieter || undefined;
}
export function getBookingNr(User) {
    const { storageKeyBookingNr } = Config;
    let value = Store.get(storageKeyBookingNr);
    if (!value && User.jwt && !User.isAgency()) {
        value = jwtDecode(User.jwt).sub || undefined;
    }
    return parseInt(value) ? parseInt(value) : undefined;
}
export function getBookingName(booking) {
    const { storageKeySurname } = Config;
    let value = Store.get(storageKeySurname) || undefined;
    if (!value && booking) {
        value = getSurnameFromBooking(booking) || undefined;
    }
    return value;
}
export function getCruisers(booking, asMap = false) {
    const ships = new Array();
    const shipsMap = {};
    const travs = booking["buchung/reiseteilnehmer"] || [];
    travs.forEach((trav) => {
        const cruises = trav["reiseteilnehmer/kreuzfahrten"] || [];
        cruises.forEach((cruise) => {
            const ship = cruise["kreuzfahrt/schiff"] || null;
            if (ship && ship["schiff/name"]) {
                const name = ship["schiff/name"];
                const abbr = ship["schiff/kuerzel"];
                if (!ships.includes(name)) {
                    ships.push(name);
                    shipsMap[abbr] = name;
                }
            }
        });
    });
    return asMap ? shipsMap : ships;
}
export function removeBookingNr() {
    Store.remove(Config.storageKeyBookingNr);
}
export function getSurnameFromBooking(booking) {
    const travs = booking["buchung/reiseteilnehmer"] ?? null;
    if (travs?.length > 0) {
        const trav = travs[0];
        return trav["reiseteilnehmer/nachname"];
    }
    return "";
}
export async function getPhoenixJWT(abbr, pwd) {
    const { storageKeyJWT, storageKeyUserAbbr } = Config;
    return requestAPI("phxauth.auth-mitarbeiter", {
        "remote-addr": "remote-addr",
        "benutzer-kennung": abbr,
        passwort: pwd,
    }).then((result) => {
        Store.set(storageKeyJWT, result.jwt);
        Store.set(storageKeyUserAbbr, abbr);
        return result;
    });
}
export async function getCustomerJWT(booknr, surname) {
    const { storageKeyJWT, storageKeyBookingNr, storageKeySurname } = Config;
    return requestAPI("phxauth.auth-buchung", {
        nachname: surname,
        "rech-nr": booknr.toString(),
        "remote-addr": "remote-addr",
    }).then((result) => {
        if (result?.jwt) {
            Store.set(storageKeyJWT, result.jwt);
            Store.set(storageKeySurname, surname);
            Store.set(storageKeyBookingNr, booknr);
        }
        return result;
    });
}
export async function auth(first, second) {
    if (isPhxAbbr(first)) {
        const abbr = first;
        const pwd = second;
        return getPhoenixJWT(abbr, pwd);
    }
    const surname = second;
    const rechnr = parseInt(first);
    return getCustomerJWT(rechnr, surname);
}
export async function authAgency(agencyid) {
    const url = Config.env.EMAIL_LOGIN.replace("{agentur-nr}", agencyid);
    return requestAPI("phxauth.agentur-login-email", {
        "remote-addr": "set by backend",
        type: "send-agentur-login-email",
        "webtext-key-subject-template": "login__agentur_email__subject",
        "webtext-key-body-template": "login__agentur_email__body_reloaded",
        "agentur-nr": parseInt(agencyid),
        "login-url-template": url,
    }).then((result) => {
        if (result) {
            const { storageKeyAgencyNr } = Config;
            Store.set(storageKeyAgencyNr, agencyid);
        }
        return result;
    });
}
export async function unauth() {
    User.logout();
    removeBookingNr();
    Store.remove(Config.storageKeyBooking);
    Store.remove(Config.storageKeySurname);
    Store.remove(Config.storageKeyUserAbbr);
    Store.remove(Config.storageKeyAgencyNr);
    SessionStore?.remove(Config.storageKeyPermission);
}
export async function authWithPassword(booknr, surname, password) {
    if (!booknr || !surname || !password) {
        return Promise.reject("fehlende Parameter");
    }
    return requestAPI("phxauth.auth-buchung", {
        "rech-nr": booknr.toString(),
        nachname: surname.toString(),
        passwort: password.toString(),
        "remote-addr": "remote-addr",
    }).then((result) => {
        if (result?.jwt) {
            User.jwt = result.jwt;
            User.persist();
        }
        return result;
    });
}
export async function requestTwoFactorLoginCode(username, jwt) {
    return callService("phxauth.versende-two-factor-auth-code-an-mitarbeiter", {
        "remote-addr": "set by backend",
        "two-factor-auth-chan": "SMS",
        "benutzer-kennung": username,
        jwt: jwt,
    });
}
export async function loginWithTwoFactorCode(username, code, jwt) {
    return requestAPI("phxauth.pruefe-mitarbeiter-two-factor-auth-code", {
        "remote-addr": "set by backend",
        "benutzer-kennung": username,
        code: code,
        jwt: jwt,
    }).then((result) => {
        if (result?.jwt) {
            User.jwt = result.jwt;
            User.persist();
        }
        return result;
    });
}
export async function renewAuthIfNecessary() {
    if (!User.jwt || !User.jwt.length) {
        return Promise.reject("JWT not found.");
    }
    const now = Date.now();
    const props = jwtDecode(User.jwt);
    const exp = props.exp;
    const ThirtyMin = 1000 * 60 * 30;
    if (exp <= now) {
        return Promise.reject(User.jwt);
    }
    if (ThirtyMin < exp - now) {
        return Promise.resolve(User.jwt);
    }
    return callService("phxauth.renew-jwt", {
        "remote-addr": "remote-addr",
        jwt: User.jwt,
    }).then((result) => {
        if (result?.jwt) {
            User.jwt = result.jwt;
            User.persist();
            return result.jwt;
        }
        return null;
    });
}
export async function getBookingByNr(bookingNr, force = false, size = "M") {
    if (!bookingNr) {
        return Promise.resolve((Booking.data = null));
    }
    if (bookingRequest) {
        return bookingRequest;
    }
    if (!force &&
        Booking.data &&
        Booking.size &&
        ((size === "M" && Booking.size !== "M") || Booking.size.includes(size))) {
        return Promise.resolve(Booking.data);
    }
    return (bookingRequest = callService("buchung-repo.extern-get-buchung", {
        "remote-addr": "remote-addr",
        "rech-nr": bookingNr,
        jwt: User.jwt,
        size: size,
    })
        .then((result) => {
        Booking.changed = new Date();
        Booking.size = size;
        Booking.data = result;
        Store.set(Config.storageKeyBookingNr, bookingNr);
        return result;
    })
        .catch((e) => {
        const error = e;
        Booking.data = null;
        Booking.size = undefined;
        Booking.changed = undefined;
        let msg = "";
        if (error && typeof error === "string") {
            msg = error;
        }
        else if (error &&
            typeof error["userfriendly-message"] !== "undefined") {
            msg = JSON.stringify(error["userfriendly-message"]);
        }
        else if (error && error["details"] !== "undefined") {
            msg = JSON.stringify(error["details"]);
        }
        else {
            msg = "";
        }
        if (!error || Object.keys(error).length === 0) {
            throw new Error("not-found");
        }
        else if (error["type"] === "failure" &&
            error["error-code"] === "ERR__FEHLENDE_BUCHUNGS_BERECHTIGUNG") {
            throw new Error("unauthorized");
        }
        else if (error["type"] === "failure" &&
            error["error-code"] === "ERR__UNVERIFIED_JWT") {
            throw new Error("unverified");
        }
        else if (error["type"] === "failure" &&
            msg.includes("Fehlende Berechtigung")) {
            throw new Error("unauthorized");
        }
        else {
            throw new Error(`Uncatched: ${JSON.stringify(error, null, 4)}`);
        }
    })
        .finally(() => {
        persist({
            data: Booking.data,
            size: Booking.size,
            changed: Booking.changed,
        });
        bookingRequest = null;
    }));
}
export default Booking;
